import type { ContextMode } from '@/api/types/contextMode';
import { useUser } from '@/hooks/useUser';

export interface ContextModeState {
  /**
   * True while `/me` has not answered. The root already holds every route on
   * the app skeleton until it has, so below the root this is false by the
   * time anything renders; it is defence in depth for a decision made once (a
   * redirect, a stored marker) and for anything mounted above that gate,
   * where `/me` is still at its defaults. A rendered surface may read `mode`
   * at once, since it re-renders when the answer lands.
   */
  isLoading: boolean;
  mode: ContextMode;
}

/**
 * How this organization's codebases are served, from `/me`, and whether that
 * is known yet. `LITE` strips the app to Investigations: the agents work from
 * the connected source alone, so the generated-context UI hides and the API
 * refuses generation. Unknown (still loading, a backend from before the mode,
 * or a value this build does not know) reads as `LEGACY`, today's behaviour,
 * so an existing organization never sees its context UI disappear late.
 */
export function useContextModeState(): ContextModeState {
  const { contextMode, isMeLoading } = useUser();
  return { isLoading: isMeLoading, mode: contextMode };
}

/** The mode alone, for surfaces that can show it as soon as it lands. */
export function useContextMode(): ContextMode {
  return useContextModeState().mode;
}
